"use client";

import { signup } from '@/app/auth/actions'
import { LoaderButton } from '@/components/shared/core';
import { Checkbox, Input, Label } from '@/components/ui';
import { ESubscriptionPlan } from '@/constants/enums';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useActionState, useEffect } from 'react';
import { FaLinkedin } from 'react-icons/fa';
import { toast } from 'sonner';

export function SignupForm({jobURL}: { jobURL?: string }) {
    const initialState: { message: string; inputs: any, errors: any, success:boolean, redirectTo: string | null } = {
        message: '',
        inputs: {},
        errors: {},
        success: false,
        redirectTo: null,
    }

    const [formState, formAction, isPending] = useActionState(signup, initialState)
    const params = useSearchParams();
    const subscriptionPlan = params?.get('plan') || ESubscriptionPlan.FREE;
    const priceId = params?.get('price') || "";
    const stripeCancelled = params?.get('cancelled') || null;

    useEffect(() => {
        if (stripeCancelled) {
            toast("Thank you for signing up!", {
                description: "Please check your email to verify your account. You will be redirected to login shortly.",
                duration: 5000,
            })
            window.setTimeout(() => {
            window.location.href = '/login';
            }, 10000);
        }
    }, [stripeCancelled])

    useEffect(() => {
        if (formState?.success) {
            if(formState?.redirectTo) {
                toast("Thank you for signing up!", {
                    description: formState.message,
                    duration: 5000,
                })
                window.location.href = formState.redirectTo;
            }
            else {
                toast("Thank you for signing up!", {
                    description: "Please check your email to verify your account. You will be redirected to login now.",
                    duration: 5000,
                })
                window.setTimeout(() => {
                window.location.href = '/login';
                }, 5000);
            }
        } else {
            if (formState?.message) {
                toast.error(formState.message, {
                    description: "Please check the form and try again.",
                    duration: 3000,
                })
            }
        }
    }, [formState]);

    return (
        <form data-testid="signup-form" action={formAction}>
             <Input type="hidden" readOnly name="jobURL" id="joburl" defaultValue={jobURL} className="invisible"/>
             <Input type="hidden" readOnly name="subscriptionPlan" id="subscriptionPlan" defaultValue={subscriptionPlan} className="invisible"/>
             <Input type="hidden" readOnly name="priceId" id="priceId" defaultValue={priceId} className="invisible"/>
            <div className="grid gap-2 mt-4">
                <Label htmlFor="name">Name</Label>
                <Input
                    id="name"
                    type="text"
                    data-testid="signup-form-name-input"
                    placeholder="John Doe"
                    name="name"
                    className={`${(formState.errors as any)?.name ? 'border-2 border-red-500 focus:border-red-500 focus:ring-red-500':''}`}
                    defaultValue={formState?.inputs?.name}
                    required
                />
                { (formState.errors as any)?.name && (
                <p className="text-sm text-red-500 text-center py-2">{
                    (formState.errors as any)?.name?.map((error: string, index:number) => (
                        <label key={index}>{error}</label>
                    ))
                }</p>) }
            </div>
            <div className="grid gap-2 mt-2">
                <Label htmlFor="email">Email</Label>
                <Input
                    id="email"
                    type="email"
                    data-testid="signup-form-email-input"
                    placeholder="m@example.com"
                    name="email"
                    className={`${(formState.errors as any)?.email ? 'border-2 border-red-500 focus:border-red-500 focus:ring-red-500':''}`}
                    required
                    defaultValue={formState?.inputs?.email}
                />
                { (formState.errors as any)?.email && (
                <p className="text-sm text-red-500 text-center py-2">{
                    (formState.errors as any)?.email?.map((error: string, index:number) => (
                        <label key={index}>{error}</label>
                    ))
                }</p>) }
            </div>
            <div className={`grid gap-2 mt-2`}>
                <Label htmlFor="password">Password</Label>
                <Input
                    id="password"
                    type="password"
                    data-testid="signup-form-password-input"
                    name="password"
                    required
                    className={`${(formState.errors as any)?.password ? 'border-2 border-red-500 focus:border-red-500 focus:ring-red-500':''}`}
                    defaultValue={formState?.inputs?.password}
                />
                { (formState.errors as any)?.password && (
                <p className="text-sm text-red-500 text-center py-2">{
                    (formState.errors as any)?.password?.map((error: string, index:number) => (
                        <label key={index}>{error}</label>
                    ))
                }</p> )}
            </div>
             <div className="grid gap-2 mt-4">
                <div className="flex items-center space-x-2">
                <Checkbox 
                  id="terms"
                  name="terms"
                  required
                  defaultValue={formState?.inputs?.terms}
                  className={`${(formState.errors as any)?.terms ? 'border-2 border-red-500 focus:border-red-500 focus:ring-red-500':''}`}
               />
                <label
                  htmlFor="terms"
                  className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  I agree to the{' '}
                  <Link href="https://jobcompass.ai/terms-and-conditions" target="_blank" className="text-blue-600 hover:text-blue-800">
                    terms and conditions
                  </Link>{' '}
                  and{' '}
                  <Link href="https://jobcompass.ai/privacy-policy" target="_blank" className="text-blue-600 hover:text-blue-800">
                    privacy policy
                  </Link>
                </label>
                </div>
                { stripeCancelled && !formState.message && (
                    <div>
                        <p className="text-sm text-red-500 text-center py-2">Your subscription has not been processed.</p>
                        <p className="text-sm text-green-500 text-center py-2">We have created a FREE account for you. Please check your email to verify your account.</p></div>
                )}
                 { (formState.errors as any)?.terms && (
                <p className="text-sm text-red-500 text-center py-2">{
                    (formState.errors as any)?.terms?.map((error: string, index:number) => (
                        <label key={index}>{error}</label>
                    ))
                }</p>) }
              </div>
              {formState?.message && !formState.errors && !formState.success && (
                    <p className="text-sm text-red-500 text-center py-2 mt-3 mb-3">{formState.message}</p>
                )}
                {formState?.message && formState.success && (
                    <p className="text-sm text-green-500 text-center py-2 mt-3 mb-3">{formState.message}</p>
                )}
              <LoaderButton isPending={isPending} text={'Sign up'} pendingText='Submitting...'/>
            
        </form>
    )
}